/**
 * Labor Progress — Weekly ExtensisHR labor detail surfaced in Command Center.
 * Feature owner: Tom Clancy (2026-09-11).
 *
 * Data source: xlsx that Donna (AR/HR) uploads to Sage Job Cost each week.
 * Same file lands here via drag-drop upload or email intake (Donna → tclancy@genspark.email).
 *
 * House rules honored:
 *  - Every column in every table is sortable.
 *  - Columns are wide enough to read without wrapping (min-widths + horizontal scroll).
 *
 * Uses: /api/labor-progress/{summary,rows,filters,upload,overhead}
 */

import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import {
  Box, Paper, Typography, Stack, Grid, Tabs, Tab, Button, IconButton,
  TextField, FormControl, InputLabel, Select, MenuItem, Autocomplete,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel,
  Alert, CircularProgress, Chip, LinearProgress, Tooltip,
  Drawer, Divider,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { format, subDays } from 'date-fns';
import api from '../services/api';
import { useSortableTable } from '../utils/useSortableTable';

// ─── formatting helpers ────────────────────────────────────────────────────
const fmt$ = (n) => (n == null || n === '') ? '—'
  : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(n));
const fmtHrs = (n) => (n == null || n === '') ? '—' : `${Number(n).toLocaleString('en-US', { maximumFractionDigits: 1 })} h`;
const fmtInt = (n) => (n == null || n === '') ? '—' : Number(n).toLocaleString('en-US');
const fmtDate = (d) => d ? format(new Date(d), 'MMM d, yyyy') : '—';
const fmtPct = (n) => (n == null || n === '') ? '—' : `${Number(n).toFixed(1)}%`;

// ─── summary tile ──────────────────────────────────────────────────────────
const Tile = ({ label, value, sub, color, onClick }) => (
  <Paper elevation={2} sx={{ p: 1.5, cursor: onClick ? 'pointer' : 'default', '&:hover': onClick ? { boxShadow: 4 } : {} }} onClick={onClick}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="h6" fontWeight="bold" color={color || 'text.primary'}>{value}</Typography>
    {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
  </Paper>
);

// ─── column widths (all tables share these where possible for readability) ─
const COL_WIDTHS = {
  installer: 200, job: 130, project: 300, task: 100, task_label: 220,
  category: 180, kind: 120, code: 100, code_label: 260,
  hours: 110, cost: 130, count: 110, days: 100, avg: 110, date: 130,
  gl: 140,
};

// ─── Non-billable ratio color helper ───────────────────────────────────────
const nbColor = (pct) => {
  const p = Number(pct || 0);
  if (p >= 70) return 'error.main';
  if (p >= 50) return 'warning.main';
  return 'text.primary';
};

const KIND_COLORS = {
  travel: 'info', commute: 'info', warranty: 'error', downtime: 'error',
  overhead: 'warning', lost: 'error', sales: 'default', training: 'default', other: 'default',
};

export default function LaborProgress() {
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [data, setData] = useState(null);
  const [overhead, setOverhead] = useState([]);
  const [filters, setFilters] = useState({ installers: [], jobs: [], categories: [], task_codes: [] });
  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [tab, setTab] = useState(0);
  const fileInputRef = useRef(null);

  // ── job drill-down drawer ───────────────────────────────────────────────
  const [jobDrawerJob, setJobDrawerJob] = useState(null);
  const [jobDetail, setJobDetail] = useState(null);
  const [jobDetailLoading, setJobDetailLoading] = useState(false);

  // ── category drill-down drawer ──────────────────────────────────────────
  const [catDrawer, setCatDrawer] = useState(null);
  const [catDetail, setCatDetail] = useState(null);
  const [catDetailLoading, setCatDetailLoading] = useState(false);

  // ── filter state (default: last 90 days) ────────────────────────────────
  const today = new Date();
  const [dateStart, setDateStart] = useState(format(subDays(today, 90), 'yyyy-MM-dd'));
  const [dateEnd, setDateEnd] = useState(format(today, 'yyyy-MM-dd'));
  const [installer, setInstaller] = useState('');
  const [jobNumber, setJobNumber] = useState('');
  const [category, setCategory] = useState('');
  const [taskCode, setTaskCode] = useState('');
  const [bucket, setBucket] = useState('');
  const [search, setSearch] = useState('');

  const buildParams = useCallback(() => {
    const p = {};
    if (dateStart) p.start = dateStart;
    if (dateEnd) p.end = dateEnd;
    if (installer) p.installer = installer;
    if (jobNumber) p.job_number = jobNumber;
    if (category) p.category = category;
    if (taskCode) p.task_code = taskCode;
    if (bucket) p.bucket = bucket;
    return p;
  }, [dateStart, dateEnd, installer, jobNumber, category, taskCode, bucket]);

  const loadSummary = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get('/labor-progress/summary', { params: buildParams() });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setLoading(false); }
  }, [buildParams]);

  const loadFilters = useCallback(async () => {
    try {
      const res = await api.get('/labor-progress/filters');
      setFilters(res.data);
    } catch (e) { /* soft fail */ }
  }, []);

  const loadOverhead = useCallback(async () => {
    try {
      const res = await api.get('/labor-progress/overhead', { params: buildParams() });
      setOverhead(res.data.overhead || []);
    } catch (e) { /* soft fail */ }
  }, [buildParams]);

  const loadRows = useCallback(async () => {
    setRowsLoading(true);
    try {
      const res = await api.get('/labor-progress/rows', { params: { ...buildParams(), limit: 1000 } });
      setRows(res.data.rows);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setRowsLoading(false); }
  }, [buildParams]);

  const openJobDrawer = useCallback(async (jobNum) => {
    setJobDrawerJob(jobNum);
    setJobDetail(null);
    setJobDetailLoading(true);
    try {
      const res = await api.get(`/labor-progress/jobs/${encodeURIComponent(jobNum)}/detail`, {
        params: { start: dateStart, end: dateEnd },
      });
      setJobDetail(res.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setJobDetailLoading(false); }
  }, [dateStart, dateEnd]);
  const closeJobDrawer = () => { setJobDrawerJob(null); setJobDetail(null); };

  const openCatDrawer = useCallback(async (category) => {
    setCatDrawer(category); setCatDetail(null); setCatDetailLoading(true);
    try {
      const res = await api.get(`/labor-progress/categories/${encodeURIComponent(category)}/detail`, {
        params: { start: dateStart, end: dateEnd },
      });
      setCatDetail(res.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setCatDetailLoading(false); }
  }, [dateStart, dateEnd]);
  const closeCatDrawer = () => { setCatDrawer(null); setCatDetail(null); };

  useEffect(() => { loadFilters(); }, [loadFilters]);
  useEffect(() => { loadSummary(); loadOverhead(); }, [loadSummary, loadOverhead]);
  useEffect(() => { if (tab === 4) loadRows(); /* eslint-disable-next-line */ },
    [tab, dateStart, dateEnd, installer, jobNumber, category, taskCode, bucket]);

  const clearFilters = () => {
    setDateStart(format(subDays(today, 90), 'yyyy-MM-dd'));
    setDateEnd(format(today, 'yyyy-MM-dd'));
    setInstaller(''); setJobNumber(''); setCategory(''); setTaskCode(''); setBucket('');
  };

  const onFileChosen = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true); setUploadResult(null); setError(null);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await api.post('/labor-progress/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadResult(res.data);
      await loadSummary(); await loadFilters(); await loadOverhead();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // ── data prep for tables ────────────────────────────────────────────────
  const installers = useMemo(() => {
    const list = data?.by_installer || [];
    // enrich with billable vs overhead split from raw hrs — approximated by joining
    // against by_job if needed. For now, use base rows.
    const q = search.toLowerCase();
    return (q ? list.filter(r => r.name?.toLowerCase().includes(q)) : list)
      .map(r => ({
        ...r,
        avg_per_day: r.days_worked ? Number(r.hours) / Number(r.days_worked) : null,
      }));
  }, [data, search]);

  const jobs = useMemo(() => {
    const list = data?.by_job || [];
    const q = search.toLowerCase();
    return (q
      ? list.filter(r => [r.job_number, r.project_name].filter(Boolean).some(v => String(v).toLowerCase().includes(q)))
      : list
    ).map(r => ({ ...r, is_overhead: r.job_number && /^[0-9]{2}-[A-Z]{3,4}$/.test(r.job_number) }));
  }, [data, search]);

  const categories = data?.by_category || [];
  const tasks = data?.by_task || [];

  const overheadFiltered = useMemo(() => {
    if (!search) return overhead;
    const q = search.toLowerCase();
    return overhead.filter(r => [r.code, r.label, r.kind, r.job_number].filter(Boolean).some(v => String(v).toLowerCase().includes(q)));
  }, [overhead, search]);

  const installerTable = useSortableTable(installers, { defaultKey: 'hours', defaultDir: 'desc' });
  const jobTable = useSortableTable(jobs, { defaultKey: 'hours', defaultDir: 'desc' });
  const catTable = useSortableTable(categories, { defaultKey: 'hours', defaultDir: 'desc' });
  const taskTable = useSortableTable(tasks, { defaultKey: 'hours', defaultDir: 'desc' });
  const overheadTable = useSortableTable(overheadFiltered, { defaultKey: 'hours', defaultDir: 'desc' });
  const rowsTable = useSortableTable(rows, { defaultKey: 'date_in', defaultDir: 'desc' });

  const t = data?.totals || {};
  const nbPct = t.total_hours && Number(t.total_hours) > 0
    ? (Number(t.overhead_hours) / Number(t.total_hours)) * 100
    : 0;

  return (
    <Box sx={{ p: 2, maxWidth: 1800, mx: 'auto' }}>
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">Labor Progress</Typography>
          <Typography variant="caption" color="text.secondary">
            Weekly ExtensisHR labor detail — same file Donna uploads to Sage Job Cost.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <input ref={fileInputRef} type="file" accept=".xlsx" hidden onChange={onFileChosen} />
          <Button
            variant="contained"
            startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? 'Uploading…' : 'Upload weekly xlsx'}
          </Button>
          <IconButton onClick={() => { loadSummary(); loadOverhead(); }} disabled={loading}><RefreshIcon /></IconButton>
        </Stack>
      </Stack>

      {uploadResult && (
        <Alert severity="success" onClose={() => setUploadResult(null)} sx={{ mb: 2 }}>
          Imported <strong>{uploadResult.rows_inserted}</strong> new rows
          {uploadResult.rows_skipped > 0 && ` (${uploadResult.rows_skipped} duplicates skipped)`}.
          Date range: {fmtDate(uploadResult.date_start)} – {fmtDate(uploadResult.date_end)}.
          {uploadResult.matched_jobs} jobs matched, {uploadResult.unmatched_jobs} unmatched.
        </Alert>
      )}
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── Filter bar ─────────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 1.5, mb: 2 }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={6} sm={3} md={1.5}>
            <TextField label="Start" type="date" size="small" fullWidth
              InputLabelProps={{ shrink: true }} value={dateStart}
              onChange={e => setDateStart(e.target.value)} />
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <TextField label="End" type="date" size="small" fullWidth
              InputLabelProps={{ shrink: true }} value={dateEnd}
              onChange={e => setDateEnd(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Autocomplete size="small" options={filters.installers} value={installer || null}
              onChange={(_, v) => setInstaller(v || '')}
              renderInput={(p) => <TextField {...p} label="Installer" />} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Autocomplete size="small"
              options={filters.jobs.map(j => j.job_number)}
              getOptionLabel={(o) => {
                const j = filters.jobs.find(x => x.job_number === o);
                return j?.project_name ? `${o} — ${j.project_name}` : String(o);
              }}
              value={jobNumber || null}
              onChange={(_, v) => setJobNumber(v || '')}
              renderInput={(p) => <TextField {...p} label="Job #" />} />
          </Grid>
          <Grid item xs={6} sm={4} md={1.5}>
            <FormControl size="small" fullWidth>
              <InputLabel>Category</InputLabel>
              <Select label="Category" value={category} onChange={e => setCategory(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                {filters.categories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={4} md={1.5}>
            <Autocomplete size="small"
              options={filters.task_codes.map(t => t.task_code)}
              getOptionLabel={(o) => {
                const tc = filters.task_codes.find(x => x.task_code === o);
                return tc?.label ? `${o} — ${tc.label}` : String(o);
              }}
              value={taskCode || null}
              onChange={(_, v) => setTaskCode(v || '')}
              renderInput={(p) => <TextField {...p} label="Task" />} />
          </Grid>
          <Grid item xs={6} sm={4} md={1.5}>
            <FormControl size="small" fullWidth>
              <InputLabel>Bucket</InputLabel>
              <Select label="Bucket" value={bucket} onChange={e => setBucket(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="billable">Billable only</MenuItem>
                <MenuItem value="overhead">Non-billable only</MenuItem>
                <MenuItem value="unknown">Unknown</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2} md={0.5}>
            <Tooltip title="Reset filters">
              <IconButton onClick={clearFilters}><ClearIcon /></IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {/* ── Summary tiles ──────────────────────────────────────────────── */}
      {loading ? (
        <LinearProgress sx={{ mb: 2 }} />
      ) : (
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={4} md={2}><Tile label="Total Hours" value={fmtHrs(t.total_hours)} sub={fmt$(t.total_cost)} /></Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Tile label="Billable" value={fmtHrs(t.billable_hours)} sub={fmt$(t.billable_cost)} color="success.main" onClick={() => setBucket('billable')} />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Tile label="Non-Billable" value={fmtHrs(t.overhead_hours)}
              sub={`${fmt$(t.overhead_cost)} · ${fmtPct(nbPct)} of total`}
              color={nbColor(nbPct)}
              onClick={() => { setBucket('overhead'); setTab(3); }} />
          </Grid>
          <Grid item xs={6} sm={4} md={2}><Tile label="Installers" value={fmtInt(t.distinct_installers)} /></Grid>
          <Grid item xs={6} sm={4} md={2}><Tile label="Jobs / Codes" value={fmtInt(t.distinct_jobs)} /></Grid>
          <Grid item xs={6} sm={4} md={2}><Tile label="Date range"
            value={t.min_date && t.max_date ? `${fmtDate(t.min_date)} – ${fmtDate(t.max_date)}` : '—'} /></Grid>
        </Grid>
      )}

      {/* ── Search + Tabs ──────────────────────────────────────────────── */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="By Installer" />
          <Tab label="By Job" />
          <Tab label="By Category / Task" />
          <Tab label={`Non-Billable${overhead.length ? ` (${overhead.length})` : ''}`} />
          <Tab label="Raw Entries" />
        </Tabs>
        <Box sx={{ flex: 1 }} />
        <TextField size="small" placeholder="Search…" value={search}
          onChange={e => setSearch(e.target.value)} sx={{ minWidth: 240 }}
          InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} /> }} />
      </Stack>

      <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
        {/* ── Tab 0: By Installer ───────────────────────────────────── */}
        {tab === 0 && (
          <TableContainer sx={{ maxHeight: 640, overflowX: 'auto' }}>
            <Table size="small" stickyHeader sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  {installerTable.sortableCell('name', 'Installer', 'left', { minWidth: COL_WIDTHS.installer })}
                  {installerTable.sortableCell('hours', 'Hours', 'right', { minWidth: COL_WIDTHS.hours })}
                  {installerTable.sortableCell('cost', 'Cost', 'right', { minWidth: COL_WIDTHS.cost })}
                  {installerTable.sortableCell('jobs_touched', 'Jobs', 'right', { minWidth: COL_WIDTHS.count })}
                  {installerTable.sortableCell('days_worked', 'Days', 'right', { minWidth: COL_WIDTHS.days })}
                  {installerTable.sortableCell('avg_per_day', 'Avg hrs/day', 'right', { minWidth: COL_WIDTHS.avg })}
                </TableRow>
              </TableHead>
              <TableBody>
                {installerTable.sortedRows.map(r => (
                  <TableRow key={r.name} hover>
                    <TableCell sx={{ minWidth: COL_WIDTHS.installer }}>{r.name}</TableCell>
                    <TableCell align="right">{fmtHrs(r.hours)}</TableCell>
                    <TableCell align="right">{fmt$(r.cost)}</TableCell>
                    <TableCell align="right">{fmtInt(r.jobs_touched)}</TableCell>
                    <TableCell align="right">{fmtInt(r.days_worked)}</TableCell>
                    <TableCell align="right">{r.avg_per_day != null ? r.avg_per_day.toFixed(1) : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* ── Tab 1: By Job ─────────────────────────────────────────── */}
        {tab === 1 && (
          <TableContainer sx={{ maxHeight: 640, overflowX: 'auto' }}>
            <Table size="small" stickyHeader sx={{ minWidth: 1200 }}>
              <TableHead>
                <TableRow>
                  {jobTable.sortableCell('job_number', 'Job #', 'left', { minWidth: COL_WIDTHS.job })}
                  {jobTable.sortableCell('project_name', 'Project', 'left', { minWidth: COL_WIDTHS.project })}
                  {jobTable.sortableCell('is_overhead', 'Type', 'left', { minWidth: 110 })}
                  {jobTable.sortableCell('hours', 'Hours', 'right', { minWidth: COL_WIDTHS.hours })}
                  {jobTable.sortableCell('cost', 'Cost', 'right', { minWidth: COL_WIDTHS.cost })}
                  {jobTable.sortableCell('installers', 'Installers', 'right', { minWidth: COL_WIDTHS.count })}
                  {jobTable.sortableCell('first_date', 'First', 'left', { minWidth: COL_WIDTHS.date })}
                  {jobTable.sortableCell('last_date', 'Last', 'left', { minWidth: COL_WIDTHS.date })}
                </TableRow>
              </TableHead>
              <TableBody>
                {jobTable.sortedRows.map(r => (
                  <TableRow key={r.job_number} hover
                    onClick={() => openJobDrawer(r.job_number)}
                    sx={{ cursor: 'pointer' }}>
                    <TableCell sx={{ color: 'primary.main', fontWeight: 600, textDecoration: 'underline' }}>
                      {r.job_number}
                    </TableCell>
                    <TableCell>{r.project_name || <em style={{ color: '#999' }}>no project match</em>}</TableCell>
                    <TableCell>
                      {r.is_overhead
                        ? <Chip size="small" label="overhead" color="warning" variant="outlined" />
                        : <Chip size="small" label="billable" color="success" variant="outlined" />}
                    </TableCell>
                    <TableCell align="right">{fmtHrs(r.hours)}</TableCell>
                    <TableCell align="right">{fmt$(r.cost)}</TableCell>
                    <TableCell align="right">{fmtInt(r.installers)}</TableCell>
                    <TableCell>{fmtDate(r.first_date)}</TableCell>
                    <TableCell>{fmtDate(r.last_date)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* ── Tab 2: By Category / Task ─────────────────────────────── */}
        {tab === 2 && (
          <Grid container>
            <Grid item xs={12} md={4} sx={{ borderRight: { md: '1px solid #eee' } }}>
              <TableContainer sx={{ maxHeight: 640, overflowX: 'auto' }}>
                <Table size="small" stickyHeader sx={{ minWidth: 420 }}>
                  <TableHead><TableRow>
                    {catTable.sortableCell('category', 'Category', 'left', { minWidth: COL_WIDTHS.category })}
                    {catTable.sortableCell('hours', 'Hours', 'right', { minWidth: COL_WIDTHS.hours })}
                    {catTable.sortableCell('cost', 'Cost', 'right', { minWidth: COL_WIDTHS.cost })}
                  </TableRow></TableHead>
                  <TableBody>
                    {catTable.sortedRows.map(r => (
                      <TableRow key={r.category} hover
                        onClick={() => openCatDrawer(r.category)}
                        sx={{ cursor: 'pointer' }}>
                        <TableCell sx={{ color: 'primary.main', fontWeight: 600, textDecoration: 'underline' }}>
                          {r.category}
                        </TableCell>
                        <TableCell align="right">{fmtHrs(r.hours)}</TableCell>
                        <TableCell align="right">{fmt$(r.cost)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid item xs={12} md={8}>
              <TableContainer sx={{ maxHeight: 640, overflowX: 'auto' }}>
                <Table size="small" stickyHeader sx={{ minWidth: 780 }}>
                  <TableHead><TableRow>
                    {taskTable.sortableCell('task_code', 'Task Code', 'left', { minWidth: COL_WIDTHS.task })}
                    {taskTable.sortableCell('label', 'Label', 'left', { minWidth: COL_WIDTHS.task_label })}
                    {taskTable.sortableCell('kind', 'Kind', 'left', { minWidth: COL_WIDTHS.kind })}
                    {taskTable.sortableCell('hours', 'Hours', 'right', { minWidth: COL_WIDTHS.hours })}
                    {taskTable.sortableCell('cost', 'Cost', 'right', { minWidth: COL_WIDTHS.cost })}
                  </TableRow></TableHead>
                  <TableBody>
                    {taskTable.sortedRows.map(r => (
                      <TableRow key={r.task_code} hover>
                        <TableCell>{r.task_code}</TableCell>
                        <TableCell>{r.label || <em style={{ color: '#bbb' }}>unlabeled — send file to Donna</em>}</TableCell>
                        <TableCell>{r.kind ? <Chip size="small" label={r.kind} color={KIND_COLORS[r.kind] || 'default'} /> : '—'}</TableCell>
                        <TableCell align="right">{fmtHrs(r.hours)}</TableCell>
                        <TableCell align="right">{fmt$(r.cost)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        )}

        {/* ── Tab 3: Non-Billable ───────────────────────────────────── */}
        {tab === 3 && (
          <TableContainer sx={{ maxHeight: 640, overflowX: 'auto' }}>
            <Table size="small" stickyHeader sx={{ minWidth: 1100 }}>
              <TableHead>
                <TableRow>
                  {overheadTable.sortableCell('code', 'Code', 'left', { minWidth: COL_WIDTHS.code })}
                  {overheadTable.sortableCell('job_number', 'Job #', 'left', { minWidth: COL_WIDTHS.job })}
                  {overheadTable.sortableCell('label', 'Label', 'left', { minWidth: COL_WIDTHS.code_label })}
                  {overheadTable.sortableCell('kind', 'Kind', 'left', { minWidth: COL_WIDTHS.kind })}
                  {overheadTable.sortableCell('hours', 'Hours', 'right', { minWidth: COL_WIDTHS.hours })}
                  {overheadTable.sortableCell('cost', 'Cost', 'right', { minWidth: COL_WIDTHS.cost })}
                  {overheadTable.sortableCell('installers', 'Installers', 'right', { minWidth: COL_WIDTHS.count })}
                  {overheadTable.sortableCell('rows', 'Rows', 'right', { minWidth: COL_WIDTHS.count })}
                </TableRow>
              </TableHead>
              <TableBody>
                {overheadTable.sortedRows.map(r => (
                  <TableRow
                    key={r.job_number}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => openJobDrawer(r.job_number)}
                    title={`Click to see which installers billed to ${r.job_number}`}
                  >
                    <TableCell><strong>{r.code}</strong></TableCell>
                    <TableCell sx={{ color: 'primary.main', textDecoration: 'underline' }}>{r.job_number}</TableCell>
                    <TableCell>{r.label || <em style={{ color: '#bbb' }}>unlabeled</em>}</TableCell>
                    <TableCell>{r.kind ? <Chip size="small" label={r.kind} color={KIND_COLORS[r.kind] || 'default'} /> : '—'}</TableCell>
                    <TableCell align="right">{fmtHrs(r.hours)}</TableCell>
                    <TableCell align="right">{fmt$(r.cost)}</TableCell>
                    <TableCell align="right">{fmtInt(r.installers)}</TableCell>
                    <TableCell align="right">{fmtInt(r.rows)}</TableCell>
                  </TableRow>
                ))}
                {overheadTable.sortedRows.length === 0 && (
                  <TableRow><TableCell colSpan={8} sx={{ textAlign: 'center', fontStyle: 'italic', color: 'text.secondary' }}>
                    No non-billable hours in the current filter window.
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* ── Tab 4: Raw Entries ────────────────────────────────────── */}
        {tab === 4 && (
          rowsLoading ? <LinearProgress /> : (
            <TableContainer sx={{ maxHeight: 640, overflowX: 'auto' }}>
              <Table size="small" stickyHeader sx={{ minWidth: 1300 }}>
                <TableHead>
                  <TableRow>
                    {rowsTable.sortableCell('date_in', 'Date', 'left', { minWidth: COL_WIDTHS.date })}
                    {rowsTable.sortableCell('employee_name', 'Installer', 'left', { minWidth: COL_WIDTHS.installer })}
                    {rowsTable.sortableCell('job_number', 'Job #', 'left', { minWidth: COL_WIDTHS.job })}
                    {rowsTable.sortableCell('billable_bucket', 'Bucket', 'left', { minWidth: 110 })}
                    {rowsTable.sortableCell('category', 'Category', 'left', { minWidth: COL_WIDTHS.category })}
                    {rowsTable.sortableCell('task_code', 'Task', 'left', { minWidth: COL_WIDTHS.task })}
                    {rowsTable.sortableCell('hours', 'Hours', 'right', { minWidth: COL_WIDTHS.hours })}
                    {rowsTable.sortableCell('cost', 'Cost', 'right', { minWidth: COL_WIDTHS.cost })}
                    {rowsTable.sortableCell('gl_debit', 'GL Debit', 'left', { minWidth: COL_WIDTHS.gl })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rowsTable.sortedRows.map(r => (
                    <TableRow key={r.id} hover>
                      <TableCell>{fmtDate(r.date_in)}</TableCell>
                      <TableCell>{r.employee_name}</TableCell>
                      <TableCell>{r.job_number}</TableCell>
                      <TableCell>
                        {r.billable_bucket === 'billable'
                          ? <Chip size="small" label="billable" color="success" variant="outlined" />
                          : r.billable_bucket === 'overhead'
                          ? <Chip size="small" label="overhead" color="warning" variant="outlined" />
                          : <Chip size="small" label="?" variant="outlined" />}
                      </TableCell>
                      <TableCell>{r.category}</TableCell>
                      <TableCell>{r.task_code}</TableCell>
                      <TableCell align="right">{fmtHrs(r.hours)}</TableCell>
                      <TableCell align="right">{fmt$(r.cost)}</TableCell>
                      <TableCell><code style={{ fontSize: '0.75rem' }}>{r.gl_debit}</code></TableCell>
                    </TableRow>
                  ))}
                  {rowsTable.sortedRows.length === 1000 && (
                    <TableRow><TableCell colSpan={9} sx={{ textAlign: 'center', fontStyle: 'italic', color: 'text.secondary' }}>
                      Showing first 1,000 rows — narrow your filters to see more.
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )
        )}
      </Paper>

      {/* ── Job Drill-Down Drawer ─────────────────────────────────── */}
      <Drawer anchor="right" open={!!jobDrawerJob} onClose={closeJobDrawer}
        PaperProps={{ sx: { width: { xs: '100%', md: 900 }, maxWidth: '100vw' } }}>
        <Box sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Job {jobDrawerJob}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Who billed to this job — filtered by current date range ({fmtDate(dateStart)} – {fmtDate(dateEnd)})
              </Typography>
            </Box>
            <IconButton onClick={closeJobDrawer}><CloseIcon /></IconButton>
          </Stack>
          <Divider sx={{ mb: 2 }} />

          {jobDetailLoading && <LinearProgress />}
          {jobDetail && (
            <>
              {/* summary */}
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={6} md={4}>
                  <Tile label="Project" value={jobDetail.summary.project_name || <em style={{ color: '#999' }}>no match</em>}
                    sub={jobDetail.summary.client_name || null} />
                </Grid>
                <Grid item xs={6} md={4}>
                  <Tile label="PM · Sales"
                    value={`${jobDetail.summary.pm_name || '—'} · ${jobDetail.summary.sales_rep || '—'}`}
                    sub={jobDetail.summary.dtools_progress || null} />
                </Grid>
                <Grid item xs={6} md={2}><Tile label="Hours" value={fmtHrs(jobDetail.summary.total_hours)} /></Grid>
                <Grid item xs={6} md={2}><Tile label="Cost" value={fmt$(jobDetail.summary.total_cost)} /></Grid>
                <Grid item xs={6} md={2}><Tile label="Installers" value={fmtInt(jobDetail.summary.installers)} /></Grid>
                <Grid item xs={6} md={2}><Tile label="Days worked" value={fmtInt(jobDetail.summary.days)} /></Grid>
                <Grid item xs={6} md={2}><Tile label="First" value={fmtDate(jobDetail.summary.first_date)} /></Grid>
                <Grid item xs={6} md={2}><Tile label="Last" value={fmtDate(jobDetail.summary.last_date)} /></Grid>
                {jobDetail.summary.labor_hours_sold != null && (
                  <>
                    <Grid item xs={6} md={2}><Tile label="Hrs sold" value={fmtHrs(jobDetail.summary.labor_hours_sold)} /></Grid>
                    <Grid item xs={6} md={2}><Tile label="Hrs used (D-Tools)" value={fmtHrs(jobDetail.summary.labor_hours_used)} /></Grid>
                  </>
                )}
              </Grid>

              {/* who billed — tech rollup */}
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>Who billed — by installer</Typography>
              <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, mb: 2 }}>
                <TableContainer sx={{ maxHeight: 260 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Installer</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Hours</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Cost</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Days</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>% of job</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>First</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Last</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {jobDetail.by_tech.map(r => {
                        const pct = jobDetail.summary.total_hours
                          ? (Number(r.hours) / Number(jobDetail.summary.total_hours)) * 100 : 0;
                        return (
                          <TableRow key={r.name} hover>
                            <TableCell>{r.name}</TableCell>
                            <TableCell align="right">{fmtHrs(r.hours)}</TableCell>
                            <TableCell align="right">{fmt$(r.cost)}</TableCell>
                            <TableCell align="right">{fmtInt(r.days)}</TableCell>
                            <TableCell align="right">{fmtPct(pct)}</TableCell>
                            <TableCell>{fmtDate(r.first_date)}</TableCell>
                            <TableCell>{fmtDate(r.last_date)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              {/* who billed — tech x task */}
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>Installer × task detail</Typography>
              <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <TableContainer sx={{ maxHeight: 320 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Installer</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Task</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Label</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Kind</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Hours</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Cost</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {jobDetail.by_tech_task.map((r, i) => (
                        <TableRow key={i} hover>
                          <TableCell>{r.name}</TableCell>
                          <TableCell>{r.task_code}</TableCell>
                          <TableCell>{r.task_label || <em style={{ color: '#bbb' }}>unlabeled</em>}</TableCell>
                          <TableCell>{r.task_kind ? <Chip size="small" label={r.task_kind} color={KIND_COLORS[r.task_kind] || 'default'} /> : '—'}</TableCell>
                          <TableCell>{r.category}</TableCell>
                          <TableCell align="right">{fmtHrs(r.hours)}</TableCell>
                          <TableCell align="right">{fmt$(r.cost)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </>
          )}
        </Box>
      </Drawer>

      {/* ── Category Drill-Down Drawer ────────────────────────────── */}
      <Drawer anchor="right" open={!!catDrawer} onClose={closeCatDrawer}
        PaperProps={{ sx: { width: { xs: '100%', md: 900 }, maxWidth: '100vw' } }}>
        <Box sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Box>
              <Typography variant="h6" fontWeight="bold">Category: {catDrawer}</Typography>
              <Typography variant="caption" color="text.secondary">
                Who billed — {fmtDate(dateStart)} – {fmtDate(dateEnd)}
              </Typography>
            </Box>
            <IconButton onClick={closeCatDrawer}><CloseIcon /></IconButton>
          </Stack>
          <Divider sx={{ mb: 2 }} />

          {catDetailLoading && <LinearProgress />}
          {catDetail && (
            <>
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={6} md={2}><Tile label="Hours" value={fmtHrs(catDetail.summary.hours)} /></Grid>
                <Grid item xs={6} md={2}><Tile label="Cost" value={fmt$(catDetail.summary.cost)} /></Grid>
                <Grid item xs={6} md={2}><Tile label="Installers" value={fmtInt(catDetail.summary.installers)} /></Grid>
                <Grid item xs={6} md={2}><Tile label="Jobs" value={fmtInt(catDetail.summary.jobs)} /></Grid>
                <Grid item xs={6} md={2}><Tile label="First" value={fmtDate(catDetail.summary.first_date)} /></Grid>
                <Grid item xs={6} md={2}><Tile label="Last" value={fmtDate(catDetail.summary.last_date)} /></Grid>
              </Grid>

              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>Who billed — by installer</Typography>
              <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, mb: 2 }}>
                <TableContainer sx={{ maxHeight: 260 }}>
                  <Table size="small" stickyHeader>
                    <TableHead><TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Installer</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Hours</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Cost</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Jobs</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Days</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>% of category</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {catDetail.by_tech.map(r => {
                        const pct = catDetail.summary.hours ? (Number(r.hours) / Number(catDetail.summary.hours)) * 100 : 0;
                        return (
                          <TableRow key={r.name} hover>
                            <TableCell>{r.name}</TableCell>
                            <TableCell align="right">{fmtHrs(r.hours)}</TableCell>
                            <TableCell align="right">{fmt$(r.cost)}</TableCell>
                            <TableCell align="right">{fmtInt(r.jobs)}</TableCell>
                            <TableCell align="right">{fmtInt(r.days)}</TableCell>
                            <TableCell align="right">{fmtPct(pct)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>Top jobs in this category</Typography>
              <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, mb: 2 }}>
                <TableContainer sx={{ maxHeight: 240 }}>
                  <Table size="small" stickyHeader>
                    <TableHead><TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Job #</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Project</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Hours</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Cost</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Installers</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {catDetail.by_job.map(r => (
                        <TableRow key={r.job_number} hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => { closeCatDrawer(); openJobDrawer(r.job_number); }}>
                          <TableCell sx={{ color: 'primary.main', textDecoration: 'underline' }}>{r.job_number}</TableCell>
                          <TableCell>{r.project_name || <em style={{ color: '#999' }}>overhead / no match</em>}</TableCell>
                          <TableCell align="right">{fmtHrs(r.hours)}</TableCell>
                          <TableCell align="right">{fmt$(r.cost)}</TableCell>
                          <TableCell align="right">{fmtInt(r.installers)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>Task-code mix</Typography>
              <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <TableContainer sx={{ maxHeight: 240 }}>
                  <Table size="small" stickyHeader>
                    <TableHead><TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Task</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Label</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Kind</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Hours</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Cost</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {catDetail.by_task.map(r => (
                        <TableRow key={r.task_code} hover>
                          <TableCell>{r.task_code}</TableCell>
                          <TableCell>{r.label || <em style={{ color: '#bbb' }}>unlabeled</em>}</TableCell>
                          <TableCell>{r.kind ? <Chip size="small" label={r.kind} color={KIND_COLORS[r.kind] || 'default'} /> : '—'}</TableCell>
                          <TableCell align="right">{fmtHrs(r.hours)}</TableCell>
                          <TableCell align="right">{fmt$(r.cost)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </>
          )}
        </Box>
      </Drawer>
    </Box>
  );
}
